/*! @license MIT
 * Copyright (c) 2025 Nous Research
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */
/** Resolution semantics adapted from hermes_cli/personality.py; see personality-builtins.ts for MIT notice. */
import { BUILTIN_PERSONALITIES } from "./personality-builtins.ts";

export type PersonalityText = string | readonly string[];
export type PersonalityDefinition = PersonalityText | {
  readonly systemPrompt?: PersonalityText;
  readonly description?: string;
  readonly tone?: string;
  readonly style?: string;
};
export type Personalities = Readonly<Record<string, PersonalityDefinition>>;

export function normalizePersonalityName(value: string | null | undefined): string {
  const name = (value ?? "").trim().toLowerCase();
  return ["", "none", "default", "neutral"].includes(name) ? "" : name;
}

function promptText(value: PersonalityText | undefined): string {
  return typeof value === "string" ? value.trim() : (value ?? []).map((part) => part.trim()).filter(Boolean).join("\n");
}

export function renderPersonalityPrompt(value: PersonalityDefinition): string {
  if (typeof value === "string" || Array.isArray(value)) return promptText(value);
  const structured = value as Exclude<PersonalityDefinition, PersonalityText>;
  return [promptText(structured.systemPrompt), structured.tone ? `Tone: ${structured.tone}` : "", structured.style ? `Style: ${structured.style}` : ""].map((part) => part.trim()).filter(Boolean).join("\n");
}

export function availablePersonalities(custom?: Personalities | null): Personalities {
  return Object.assign(Object.create(null) as Record<string, PersonalityDefinition>, BUILTIN_PERSONALITIES, custom);
}

export function describePersonality(value: PersonalityDefinition): string {
  const preview = (typeof value === "object" && !Array.isArray(value) && "description" in value ? value.description : "") || renderPersonalityPrompt(value);
  const flat = preview.trim().replace(/\n/g, " ");
  return flat.slice(0, 50) + (flat.length > 50 ? "..." : "");
}

/** A selected overlay replaces the manual overlay. Clearing it restores the exact user-owned prompt. */
export function agentSystemPrompt(agent: { readonly systemPrompt: string | null; readonly personality?: string | null; readonly personalities?: Personalities | null }): string | null {
  const name = normalizePersonalityName(agent.personality);
  const definition = name ? availablePersonalities(agent.personalities)[name] : undefined;
  return definition === undefined ? agent.systemPrompt : renderPersonalityPrompt(definition) || null;
}
